// https://www.chartjs.org/docs/latest/samples/tooltip/html.html
import { TooltipModel, Chart, ChartTypeRegistry } from 'chart.js';
import { ChartPointTyped } from 'src/app/services/rest-api/rest-api';
import { transformPriceForChartTooltip } from '../functions';

export enum hooverTypes {
  'depositsLinePoint' = 'depositsLinePoint',
  'chartValue' = 'chartValue',
  'deposit' = 'deposit',
}

const getOrCreateTooltip = (chart: Chart) => {
  if(!chart.canvas.parentNode) {
    throw new Error('Chart tooltip rendering error!');
  }

  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    defaultTooltipContainerStyle(tooltipEl);

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

export const externalTooltipHandler = (
  context: {chart: Chart, tooltip: TooltipModel<keyof ChartTypeRegistry>},
  type:'instrumentChart' | 'basicChart' | 'developmentChart',
  isPercent = false,
  minOneDecimalPercent?: boolean,
): void => {
  const {chart, tooltip} = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map(b => b.lines);

    const tableHead = document.createElement('thead');

    if (type === 'basicChart') {
      makeBasicHeadings(titleLines, tableHead);
    }

    const tableBody = document.createElement('tbody');
    bodyLines.forEach((body: string[], index: number) => {

      const headerTr = document.createElement('tr');
      defaultCellStyle(headerTr);
      headerTr.style.backgroundColor = 'inherit';

      const headerTd = document.createElement('td');
      headerTd.style.borderWidth = '0';

      if(type === 'basicChart') {
        createBasicChartTooltip(body, headerTd, headerTr, tableBody, tooltip, isPercent, index, minOneDecimalPercent);
      } else if (type === 'instrumentChart') {
        createInstrumentChartTooltip(tooltip, index, body, headerTr, headerTd, tableBody);
      } else if (type === 'developmentChart') {
        createDevelopmentChartTooltip(tooltip, body, headerTr, headerTd, tableBody);
      }
    });

    const tableRoot = tooltipEl.querySelector('table');

    if(tableRoot) {
      // Remove old children
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }

      // Add new children
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }
  }

  const {offsetLeft: positionX, offsetTop: positionY} = chart.canvas;

  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';

  // handle overflowing position
  if((window.innerWidth - parseInt(tooltipEl.style.left)) < 60) {
    tooltipEl.style.left = positionX + tooltip.caretX - 60 + 'px';
    tooltipEl.style.top = positionY + tooltip.caretY - 50 + 'px';
  }

  tooltipEl.style.padding = '14px 25px';
};

function defaultTooltipContainerStyle(tooltipEl: HTMLDivElement) {
  tooltipEl.style.background = 'transparent linear-gradient(180deg, #FEFEFE 0%, #F6F6F9 100%) 0% 0% no-repeat padding-box';
  tooltipEl.style.boxShadow = '0px 2px 6px #26262663';
  tooltipEl.style.borderRadius = '20px';
  tooltipEl.style.color = '#262626';
  tooltipEl.style.opacity = '1';
  tooltipEl.style.pointerEvents = 'none';
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.transform = 'translate(-50%, 0)';
  tooltipEl.style.transition = 'all .1s ease';
  tooltipEl.style.zIndex = '5';
  tooltipEl.style.whiteSpace = 'nowrap';
}

function defaultCellStyle(element: HTMLTableRowElement | HTMLTableCellElement) {
  element.style.borderWidth = '0';
  element.style.textAlign = 'center';
  element.style.font = 'normal normal 600 18px/24px Segoe UI';
  element.style.color = '#858585';
}

function valueCellStyle(element: HTMLTableCellElement) {
  element.style.borderWidth = '0';
  element.style.textAlign = 'center';
  element.style.font = 'normal normal 600 16px/21px Segoe UI';
}

function createDevelopmentChartTooltip(
  tooltip: TooltipModel<keyof ChartTypeRegistry>,
  body: string[],
  headerTr: HTMLTableRowElement,
  headerTd: HTMLTableCellElement,
  tableBody: HTMLTableSectionElement
) {
  const color = tooltip.dataPoints[0].dataset.backgroundColor;

  const lineType = body[0].split(':')[0];
  const value = body[0].split(':')[1];

  const headerText = document.createTextNode(lineType);

  const valueTr = document.createElement('tr');
  defaultTrStyle(valueTr);
  headerTr.style.font = 'normal normal normal 11px/15px Segoe UI';
  headerTr.style.color = color as string;

  const valueTd = document.createElement('td');
  valueCellStyle(valueTd);
  valueTd.style.color = color as string;
  valueTd.style.whiteSpace = 'nowrap';

  const valueText = document.createTextNode(value.trim() + ' Kč');

  headerTd.appendChild(headerText);

  headerTr.appendChild(headerTd);
  tableBody.appendChild(headerTr);

  valueTd.appendChild(valueText);
  valueTr.appendChild(valueTd);
  tableBody.appendChild(valueTr);
}

function defaultTrStyle(valueTr: HTMLTableRowElement) {
  valueTr.style.backgroundColor = 'inherit';
  valueTr.style.borderWidth = '0';
}

function createInstrumentChartTooltip(
  tooltip: TooltipModel<keyof ChartTypeRegistry>,
  index: number,
  body: string[],
  headerTr: HTMLTableRowElement,
  headerTd: HTMLTableCellElement,
  tableBody: HTMLTableSectionElement
) {
  const color = tooltip.labelColors[index].backgroundColor;

  const categoryName = body[0].split(':')[0];
  const value = body[0].split(':')[1];

  const headerText = document.createTextNode(categoryName);

  const valueTr = document.createElement('tr');
  defaultTrStyle(valueTr);
  headerTr.style.font = 'normal normal normal 11px/15px Segoe UI';
  headerTr.style.color = color as string;

  const valueTd = document.createElement('td');
  valueCellStyle(valueTd);
  valueTd.style.color = color as string;

  const valueText = document.createTextNode(value.trim() + ' %');

  headerTd.appendChild(headerText);
  headerTr.appendChild(headerTd);
  tableBody.appendChild(headerTr);
  valueTd.appendChild(valueText);
  valueTr.appendChild(valueTd);
  tableBody.appendChild(valueTr);
}

function createBasicChartTooltip(
  body: string[],
  headerTd: HTMLTableCellElement,
  headerTr: HTMLTableRowElement,
  tableBody: HTMLTableSectionElement,
  tooltip: TooltipModel<keyof ChartTypeRegistry>,
  isPercent = false,
  index?: number,
  minOneDecimalPercent?: boolean
) {
  const rawValue = tooltip.dataPoints[0]?.raw as ChartPointTyped;
  const deposit = (tooltip.dataPoints[0]?.raw as ChartPointTyped).depositValue;

  if(rawValue.type === hooverTypes.depositsLinePoint) {
    const headerText = document.createTextNode('Vloženo');

    const valueTr = document.createElement('tr');
    defaultTrStyle(valueTr);

    const valueTd = document.createElement('td');
    defaultCellStyle(valueTd)
    valueTd.style.color = '#262626';

    //todo - currency from user
    const valueText = document.createTextNode(transformPriceForChartTooltip(body[0], 'CZK', isPercent,  'cs-CZ', minOneDecimalPercent));

    headerTd.appendChild(headerText);
    headerTr.appendChild(headerTd);
    tableBody.appendChild(headerTr);

    valueTd.appendChild(valueText);
    valueTr.appendChild(valueTd);
    tableBody.appendChild(valueTr);
    return;
  } else if(rawValue.type === hooverTypes.deposit && deposit && deposit !== 0 && index === 1) {
    const depositHeaderText = document.createTextNode(deposit < 0 ? 'Výběr' : 'Vklad');
    const depositHeaderTr = document.createElement('tr');
    depositHeaderTr.style.backgroundColor = 'inherit';
    depositHeaderTr.style.borderWidth = '0';

    const depositHeaderTd = document.createElement('td');
    defaultCellStyle(depositHeaderTd)

    const depositValueTr = document.createElement('tr');
    depositValueTr.style.backgroundColor = 'inherit';
    depositValueTr.style.borderWidth = '0';

    const depositValueTd = document.createElement('td');
    defaultCellStyle(depositValueTd);
    depositValueTd.style.color = '#262626';

    //todo - currency from user
    const valueText = document.createTextNode(transformPriceForChartTooltip(deposit, 'CZK', isPercent) + '');

    depositHeaderTd.appendChild(depositHeaderText);
    depositHeaderTr.appendChild(depositHeaderTd);
    tableBody.appendChild(depositHeaderTr);

    depositValueTd.appendChild(valueText);
    depositValueTr.appendChild(depositValueTd);
    tableBody.appendChild(depositValueTr);
    return;
  } else if(
      (rawValue.type === hooverTypes.chartValue && index === 0) ||
      (rawValue.type === hooverTypes.deposit && deposit && deposit !== 0 && index === 0 && body[0] !== '0') // hide duplicit 0 but display second row when overflowing
    ) {

    // todo - translate
    const headerText = document.createTextNode('Hodnota');

    const valueTr = document.createElement('tr');
    defaultTrStyle(valueTr);

    const valueTd = document.createElement('td');
    defaultCellStyle(valueTd)
    valueTd.style.color = '#262626';

    //todo - currency from user
    const valueText = document.createTextNode(transformPriceForChartTooltip(body[0], 'CZK', isPercent));

    headerTd.appendChild(headerText);
    headerTr.appendChild(headerTd);
    tableBody.appendChild(headerTr);

    valueTd.appendChild(valueText);
    valueTr.appendChild(valueTd);
    tableBody.appendChild(valueTr);
    return;
  } else {
    return body[0];
  }
}

function makeBasicHeadings(titleLines: string[], tableHead: HTMLTableSectionElement) {
  titleLines.forEach(date => {
    const tr = document.createElement('tr');
    tr.style.borderWidth = '0';

    const th = document.createElement('th');
    defaultCellStyle(th);
    th.style.color = '#262626';

    const dateObj = new Date(date);
    const dateForView = `${dateObj.getDate()}. ${dateObj.getMonth() + 1}. ${dateObj.getFullYear()}`;
    const text = document.createTextNode(dateForView);

    th.appendChild(text);
    tr.appendChild(th);
    tableHead.appendChild(tr);
  });
}
